/**
 * Administrator permissions types
 */

/**
 * Admin Permission enum matching Core.Enums.AdminPermission from C# backend
 * Defines granular permissions for administrators.
 * Each permission controls access to specific admin operations.
 */
export enum AdminPermission {
  // Merchant Management
  MerchantsView = 'MerchantsView',
  MerchantsCreate = 'MerchantsCreate',
  MerchantsEdit = 'MerchantsEdit',
  MerchantsDelete = 'MerchantsDelete',
  MerchantsActivate = 'MerchantsActivate',
  MerchantsDeactivate = 'MerchantsDeactivate',

  // Preferences Management
  PreferencesView = 'PreferencesView',
  PreferencesCreate = 'PreferencesCreate',
  PreferencesEdit = 'PreferencesEdit',
  PreferencesDelete = 'PreferencesDelete',
  PreferencesSetMerchantOverride = 'PreferencesSetMerchantOverride',

  // Partner Integrators
  PartnerIntegratorsView = 'PartnerIntegratorsView',
  PartnerIntegratorsCreate = 'PartnerIntegratorsCreate',
  PartnerIntegratorsEdit = 'PartnerIntegratorsEdit',
  PartnerIntegratorsDelete = 'PartnerIntegratorsDelete',
  PartnerIntegratorsManageCredentials = 'PartnerIntegratorsManageCredentials',
  PartnerIntegratorsManageRateLimits = 'PartnerIntegratorsManageRateLimits',

  // Partner Management (Integration Engine)
  PartnersView = 'PartnersView',
  PartnersCreate = 'PartnersCreate',
  PartnersEdit = 'PartnersEdit',

  // Orders
  OrdersView = 'OrdersView',

  // Metabase Dashboard Configuration
  ReportsView = 'ReportsView',

  // Marketing Images
  MarketingImagesView = 'MarketingImagesView',
  MarketingImagesUpload = 'MarketingImagesUpload',
  MarketingImagesDelete = 'MarketingImagesDelete',

  // Metabase Dashboard Configuration
  MetabaseDashboardsView = 'MetabaseDashboardsView',
  MetabaseDashboardsEdit = 'MetabaseDashboardsEdit',
  MetabaseDashboardsSync = 'MetabaseDashboardsSync',

  // Feature Flags
  FeatureFlagsView = 'FeatureFlagsView',
  FeatureFlagsManage = 'FeatureFlagsManage',

  // System Utilities
  SystemRedisAccess = 'SystemRedisAccess',
  SystemDecrypt = 'SystemDecrypt',
  SystemTriggerEvents = 'SystemTriggerEvents',

  // SMS Management
  SmsBlacklistManage = 'SmsBlacklistManage',
  SmsMerchantActivate = 'SmsMerchantActivate',
  SmsMerchantDeactivate = 'SmsMerchantDeactivate',
  SmsBillingView = 'SmsBillingView',
  SmsBillingRecompute = 'SmsBillingRecompute',
  SmsComplianceCampaignSubmit = 'SmsComplianceCampaignSubmit',
  // Saving a merchant's Vonage credentials takes its SMS live on its own subaccount, so this is
  // in no preset and has to be granted explicitly.
  SmsVonageSubaccountManage = 'SmsVonageSubaccountManage',

  // Survey Management
  SurveysIpBlacklistManage = 'SurveysIpBlacklistManage',

  // Products Management
  ProductsManage = 'ProductsManage',

  // Social Media
  SocialMediaProvidersEdit = 'SocialMediaProvidersEdit',
  SocialMediaResync = 'SocialMediaResync',
  SocialMediaChatmeterLocationsView = 'SocialMediaChatmeterLocationsView',
  SocialMediaChatmeterLocationsDeactivate = 'SocialMediaChatmeterLocationsDeactivate',
  SocialMediaReviewAuditView = 'SocialMediaReviewAuditView',

  // Objectives
  ObjectivesViewKickoffData = 'ObjectivesViewKickoffData',

  // Administrators Management
  AdministratorsView = 'AdministratorsView',
  AdministratorsCreate = 'AdministratorsCreate',
  AdministratorsEdit = 'AdministratorsEdit',
  AdministratorsManagePermissions = 'AdministratorsManagePermissions',

  // Audit Logs
  AuditLogsView = 'AuditLogsView',

  // Automation Runs
  AutomationRunsView = 'AutomationRunsView',
  AutomationRunsManage = 'AutomationRunsManage',

  // Data Subject Requests (Privacy)
  DataSubjectRequestsView = 'DataSubjectRequestsView',
  DataSubjectRequestsManage = 'DataSubjectRequestsManage',

  // Webhook Logs
  WebhookLogsView = 'WebhookLogsView',


  // User Logs
  UserLogsView = 'UserLogsView',

  // Data Replication
  DataReplicationView = 'DataReplicationView',

  // User Impersonation
  UserImpersonate = 'UserImpersonate',

  // LLM Evaluations
  EvaluationsView = 'EvaluationsView',

  // Developer Tools
  TestExecutionsView = 'TestExecutionsView',
  TestExecutionsManage = 'TestExecutionsManage',
  RunTests = 'RunTests',
  SendOrders = 'SendOrders',
  SendEvents = 'SendEvents',
  AddMerchant = 'AddMerchant',
  CleanupMerchant = 'CleanupMerchant',
  AddTestData = 'AddTestData',
  SendObjectiveEmails = 'SendObjectiveEmails',
  GenerateAIActionItems = 'GenerateAIActionItems',
  PartnerImpersonate = 'PartnerImpersonate',
  MobileSimulatorAccess = 'MobileSimulatorAccess',
  /**
   * @deprecated Grants nothing; the two generator pages are open to any TattleAdmin. Kept because
   * stored permission records still list it.
   */
  GenerateReceipt = 'GenerateReceipt',
  QrStylesManage = 'QrStylesManage',

  // Permissions Management
  PermissionsView = 'PermissionsView',
  PermissionsEdit = 'PermissionsEdit',
  PermissionsBulkEdit = 'PermissionsBulkEdit',

  // Integrations Management
  IntegrationsView = 'IntegrationsView',
  IntegrationsEdit = 'IntegrationsEdit',
  // Re-reading a partner's own configuration out of their system. Granted to
  // nobody by default, so a super admin aside it has to be given explicitly.
  PartnerIntegrationsSyncConfiguration = 'PartnerIntegrationsSyncConfiguration',

  // Email Management (Mandrill)
  EmailMessagesView = 'EmailMessagesView',
  EmailRejectsView = 'EmailRejectsView',
  EmailRejectsManage = 'EmailRejectsManage',
  EmailDomainsView = 'EmailDomainsView',
  EmailDomainsManage = 'EmailDomainsManage',
  EmailBlacklistManage = 'EmailBlacklistManage',
  // Billing Rates Management
  BillingRatesView = 'BillingRatesView',
  BillingRatesEditDefault = 'BillingRatesEditDefault',
  BillingRatesEditMerchant = 'BillingRatesEditMerchant',

  // Message Template Localized Defaults
  MessageTemplateDefaultsView = 'MessageTemplateDefaultsView',
  MessageTemplateDefaultsManage = 'MessageTemplateDefaultsManage'
}

/**
 * Permission metadata for display and grouping
 */
export interface PermissionMetadata {
  name: string
  value: AdminPermission
  category: string
  description: string
}

/**
 * Response containing all available permissions with metadata
 */
export interface AvailablePermissionsResponse {
  permissions: PermissionMetadata[]
  categories: string[]
}

/**
 * Response containing an administrator's permissions
 */
export interface AdministratorPermissionsResponse {
  administratorId: number
  administratorName: string
  administratorEmail: string
  isSuperAdmin: boolean
  permissions: AdminPermission[]
  lastModifiedAt: string
  lastModifiedBy: number
}

/**
 * Request to create permissions for a new administrator
 */
export interface CreateAdministratorPermissionsRequest {
  permissions: AdminPermission[]
  isSuperAdmin?: boolean
}

/**
 * Request to update permissions for an existing administrator
 */
export interface UpdateAdministratorPermissionsRequest {
  permissions: AdminPermission[]
  isSuperAdmin?: boolean
}

/**
 * Request to grant or revoke a single permission
 */
export interface GrantPermissionRequest {
  permission: AdminPermission
  grant: boolean
}

/**
 * Request to set super admin status
 */
export interface SetSuperAdminRequest {
  isSuperAdmin: boolean
}

/**
 * Permission preset for role-based assignment
 */
export interface PermissionPreset {
  id: string
  name: string
  description: string
  permissions: AdminPermission[]
}

/**
 * Pre-defined permission presets by role
 */
/**
 * Props for ManagePermissionsModal component
 */
export interface ManagePermissionsModalProps {
  show: boolean
  administratorId: number | null
  administratorName: string
  administratorEmail: string
}

export const PERMISSION_PRESETS: PermissionPreset[] = [
  {
    id: 'engineer',
    name: 'Engineer',
    description: 'Full dev tools, system access, logs, and debugging capabilities',
    permissions: [
      AdminPermission.MerchantsView,
      AdminPermission.MerchantsEdit,
      AdminPermission.MerchantsActivate,
      AdminPermission.MerchantsDeactivate,
      AdminPermission.PreferencesView,
      AdminPermission.PreferencesEdit,
      AdminPermission.PartnerIntegratorsView,
      AdminPermission.PartnersView,
      AdminPermission.OrdersView,
      AdminPermission.FeatureFlagsView,
      AdminPermission.FeatureFlagsManage,
      AdminPermission.SystemRedisAccess,
      AdminPermission.SystemDecrypt,
      AdminPermission.SystemTriggerEvents,
      AdminPermission.AuditLogsView,
      AdminPermission.WebhookLogsView,
      AdminPermission.UserLogsView,
      AdminPermission.UserImpersonate,
      AdminPermission.EvaluationsView,
      AdminPermission.TestExecutionsView,
      AdminPermission.TestExecutionsManage,
      AdminPermission.RunTests,
      AdminPermission.SendOrders,
      AdminPermission.SendEvents,
      AdminPermission.AddMerchant,
      AdminPermission.CleanupMerchant,
      AdminPermission.AddTestData,
      AdminPermission.SendObjectiveEmails,
      AdminPermission.GenerateAIActionItems,
      AdminPermission.PartnerImpersonate,
      AdminPermission.MobileSimulatorAccess,
      AdminPermission.PermissionsView,
      AdminPermission.IntegrationsView,
      AdminPermission.IntegrationsEdit,
      AdminPermission.EmailMessagesView,
      AdminPermission.EmailRejectsView,
      AdminPermission.EmailRejectsManage,
      AdminPermission.EmailDomainsView,
      AdminPermission.EmailDomainsManage,
      AdminPermission.AutomationRunsView,
      AdminPermission.SocialMediaReviewAuditView,
      AdminPermission.ProductsManage,
      AdminPermission.DataReplicationView
    ]
  },
  {
    id: 'cs',
    name: 'Customer Support',
    description: 'Merchant management, user impersonation, SMS/email management, and support views',
    permissions: [
      AdminPermission.MerchantsView,
      AdminPermission.MerchantsCreate,
      AdminPermission.MerchantsEdit,
      AdminPermission.MerchantsActivate,
      AdminPermission.MerchantsDeactivate,
      AdminPermission.PreferencesView,
      AdminPermission.PartnerIntegratorsView,
      AdminPermission.PartnersView,
      AdminPermission.OrdersView,
      AdminPermission.ProductsManage,
      AdminPermission.MarketingImagesView,
      AdminPermission.MetabaseDashboardsView,
      AdminPermission.ReportsView,
      AdminPermission.FeatureFlagsView,
      AdminPermission.ObjectivesViewKickoffData,
      AdminPermission.WebhookLogsView,
      AdminPermission.BillingRatesView,
      AdminPermission.AdministratorsView,
      AdminPermission.EmailMessagesView,
      AdminPermission.EmailRejectsView,
      AdminPermission.EmailRejectsManage,
      AdminPermission.EmailDomainsView,
      AdminPermission.EmailDomainsManage,
      AdminPermission.EmailBlacklistManage,
      AdminPermission.SmsBillingView,
      AdminPermission.SmsMerchantActivate,
      AdminPermission.SmsMerchantDeactivate,
      AdminPermission.SmsBlacklistManage,
      AdminPermission.SocialMediaChatmeterLocationsView,
      AdminPermission.SocialMediaChatmeterLocationsDeactivate,
      AdminPermission.UserImpersonate
    ]
  },
  {
    id: 'sales',
    name: 'Sales',
    description: 'View merchant info, dashboards, and objectives data',
    permissions: [
      AdminPermission.MerchantsView,
      AdminPermission.PreferencesView,
      AdminPermission.OrdersView,
      AdminPermission.MetabaseDashboardsView,
      AdminPermission.ObjectivesViewKickoffData
    ]
  },
  {
    id: 'product',
    name: 'Product',
    description: 'Broad view access, feature flags, analytics, and impersonation',
    permissions: [
      AdminPermission.MerchantsView,
      AdminPermission.PreferencesView,
      AdminPermission.PartnerIntegratorsView,
      AdminPermission.PartnersView,
      AdminPermission.OrdersView,
      AdminPermission.MetabaseDashboardsView,
      AdminPermission.MetabaseDashboardsEdit,
      AdminPermission.FeatureFlagsView,
      AdminPermission.FeatureFlagsManage,
      AdminPermission.AuditLogsView,
      AdminPermission.ObjectivesViewKickoffData,
      AdminPermission.UserImpersonate,
      AdminPermission.DataReplicationView
    ]
  },
  {
    id: 'design',
    name: 'Design',
    description: 'View merchants, manage marketing images, and impersonation',
    permissions: [
      AdminPermission.MerchantsView,
      AdminPermission.PreferencesView,
      AdminPermission.MarketingImagesView,
      AdminPermission.MarketingImagesUpload,
      AdminPermission.UserImpersonate
    ]
  }
]
